import { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import { memo, ReactNode, useCallback, useContext, useMemo } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { LinkAndCopy } from 'src/components/common/LinkAndCopy';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import { MarketplacePaymentStatePill } from 'src/components/common/MarketplacePaymentStatePill/MarketplacePaymentStatePill';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { PosLink } from 'src/core/POS/PosLink';
import { PosPill } from 'src/core/POS/PosPill';
import { PosPillVariant } from 'src/core/POS/PosPill/PosPill';
import { SimpleTable, Stack } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useGetGroupInvoiceCatalog } from 'src/hooks/api/useGetGroupInvoiceCatalog';
import { getUserDisplayName, useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { useUserCanUpdateSale } from 'src/hooks/useUserHasSalePermissions';
import { Detail, DetailGroup, SectionContent } from 'src/modals/common';
import { DetailSection } from 'src/modals/common/DetailSection';
import type { ModalProps } from 'src/modals/Modal';
import { GroupMarketplacePaymentDialog } from 'src/modals/SaleDetails/components/MarketplacePaymentTerms/GroupMarketplacePaymentDialog';
import * as tableStyles from 'src/modals/SaleDetails/SaleDetailsTable.css';
import { getGroupPaymentStatus } from 'src/modals/SalesGroup/getGroupPaymentStatus';
import { Table } from 'src/tables/Table';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKETPLACE_EXTERNAL_PAYMENT_TYPE_TO_CID,
  SALE_COST_TYPE_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import {
  formatDate as formatDateUtil,
  stringToUtcDate,
} from 'src/utils/dateTimeUtils';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { getMarketplacePaymentRelativeUrl } from 'src/utils/marketplacePaymentUtils';
import { formatCurrency } from 'src/utils/numberFormatter';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import {
  getSaleDetailsDeepLinkUrl,
  getSaleDetailsModalConfigWithDeepLink,
} from 'src/utils/saleUtils';
import {
  ExternalPaymentMethodType,
  MarketplacePaymentState,
  MarketplaceSaleLineItem,
  SaleCostType,
  SalesGroupDetails,
  SalesGroupTicketRow,
  UiMoney,
} from 'src/WebApiController';

import { getGroupTaxAmount } from './getGroupTaxAmount';
import { getOutstandingAmt } from './getOutstandingAmt';
import * as styles from './SalesGroupModal.css';

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

export const EMPTY = '—';

export type ModalConfig = Omit<ModalProps, 'fullscreen'>;

export function formatMoney(money: UiMoney | null | undefined): string {
  if (!money) {
    return EMPTY;
  }
  return money.disp ?? formatCurrency(money.amt, money.currency);
}

export function formatDate(date: string | null | undefined): string {
  return date ? formatDateUtil(date) : EMPTY;
}

// ────────────────────────────────────────────────────────────────────────────
// SALE — four-column card sourced from the group + a representative member sale
// (the first member): Sale Group ID (# name), Merchant, Sale Date, Created By.
// Mirrors SaleDetailsV2's YourSaleSection for merchant (MarketplaceLogo),
// sale-date formatting (site timezone, "MMM d, yyyy h:mm aa") and the
// useGetUserInfos/getUserDisplayName resolution for Created By. Replaces the old
// "Sales Group" + "Your Sale" sections; all data comes from `group`, no
// clicked-sale / active-entity context.
// ────────────────────────────────────────────────────────────────────────────

export const SaleSection = ({ group }: { group: SalesGroupDetails }) => {
  const { timeZone } = useSiteTimezoneContext();

  // Representative member sale drives Merchant / Sale Date / Created By.
  const representativeSale = group.sales?.[0];

  const saleDate = representativeSale?.saleDate ?? group.insertDate;
  const displayDate = saleDate
    ? formatInTimeZone(
        stringToUtcDate(saleDate),
        timeZone,
        'MMM d, yyyy h:mm aa',
        {
          locale: getLocaleFromLanguageOrCurrent(),
        }
      )
    : EMPTY;

  const insertBy = representativeSale?.insertBy;
  const useGetUserInfosQuery = useGetUserInfos(
    insertBy ? [insertBy] : undefined
  );
  const deactivatedText = useContent(ContentId.Deactivated);
  const insertByUser = insertBy
    ? useGetUserInfosQuery.data?.[insertBy]
    : undefined;

  return (
    <DetailSection name={<Content id={ContentId.Sale} />}>
      <SectionContent numOfColumns={4}>
        <DetailGroup>
          <Detail
            label={<Content id={ContentId.SalesGroupId} />}
            detail={
              <strong>#{group.salesGroupName || group.salesGroupId}</strong>
            }
          />
        </DetailGroup>
        <DetailGroup>
          <Detail
            label={<Content id={ContentId.Merchant} />}
            detail={<MarketplaceLogo marketplace={representativeSale?.mkp} />}
          />
        </DetailGroup>
        <DetailGroup>
          <Detail
            label={<Content id={ContentId.SaleDate} />}
            detail={displayDate}
          />
        </DetailGroup>
        <DetailGroup>
          <Detail
            label={<Content id={ContentId.CreatedBy} />}
            detail={
              insertByUser ? (
                <strong>
                  {getUserDisplayName(insertByUser, deactivatedText)}
                </strong>
              ) : (
                EMPTY
              )
            }
          />
        </DetailGroup>
      </SectionContent>
    </DetailSection>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// SUMMARY — Balance Due / Subtotal / Payment as a bordered table, label left and
// $ amount right-aligned. All values are backend aggregates. Balance Due prefers
// the backend balanceDue, falling back to subtotal − paymentReceived (mirrors the
// PaymentSection's outstanding computation) and is rendered bold. A "Balance Due"
// badge sits beside the section label whenever there is an outstanding balance.
// ────────────────────────────────────────────────────────────────────────────

export const SummarySection = ({ group }: { group: SalesGroupDetails }) => {
  // Outstanding balance, computed from components (includes tax — unlike the
  // backend balanceDue). Shared with PaymentSection via getOutstandingAmt.
  const outstandingAmt = getOutstandingAmt(group);
  const hasBalanceDue = outstandingAmt > 0;

  // Render the Balance Due ROW from the SAME outstandingAmt the badge gate uses so
  // the two can never disagree — do NOT fall back to the backend balanceDue, which
  // excludes tax and is clamped. Spread a reference UiMoney (balanceDue or subtotal,
  // for currency/dec) with the computed amt and disp: null so formatMoney reformats
  // the outstanding amount (the { ...money, amt } pattern used elsewhere).
  const reference = group.balanceDue ?? group.subtotal;
  const balanceDueAmount: UiMoney | null = reference
    ? { ...reference, amt: outstandingAmt, disp: null }
    : null;

  // POS-8632: surface charges/credits (from earningLines) in the Summary table so
  // e.g. a $100 charge doesn't silently inflate Payment vs Subtotal with no
  // explanation. Charges are shown as positive; credits as negative (they reduce
  // the amount owed).
  const chargesTotal = (group.earningLines ?? [])
    .filter((line) => line.costType !== SaleCostType.Credit)
    .reduce((sum, line) => sum + Math.abs(line.amount?.amt ?? 0), 0);
  const creditsTotal = (group.earningLines ?? [])
    .filter((line) => line.costType === SaleCostType.Credit)
    .reduce((sum, line) => sum - Math.abs(line.amount?.amt ?? 0), 0);

  const chargesCreditsReference = group.subtotal ?? group.balanceDue;
  const chargesAmount: UiMoney | null = chargesCreditsReference
    ? { ...chargesCreditsReference, amt: chargesTotal, disp: null }
    : null;
  const creditsAmount: UiMoney | null = chargesCreditsReference
    ? { ...chargesCreditsReference, amt: creditsTotal, disp: null }
    : null;

  return (
    <DetailSection name={<Content id={ContentId.Summary} />}>
      <div className={tableStyles.tableWrapper}>
        <SimpleTable.Table className={tableStyles.table}>
          <SimpleTable.Tbody>
            {hasBalanceDue && (
              <SummaryRow
                label={<Content id={ContentId.BalanceDue} />}
                amount={balanceDueAmount}
                isStrong
                isError
              />
            )}
            <SummaryRow
              label={<Content id={ContentId.Subtotal} />}
              amount={group.subtotal}
            />
            {chargesTotal !== 0 && (
              <SummaryRow
                label={<Content id={ContentId.Charges} />}
                amount={chargesAmount}
              />
            )}
            {creditsTotal !== 0 && (
              <SummaryRow
                label={<Content id={ContentId.Credits} />}
                amount={creditsAmount}
              />
            )}
            <SummaryRow
              label={<Content id={ContentId.Payment} />}
              amount={group.totalPaymentReceived}
              isStrong
            />
            {/* POS-8726: Refund row — only visible when reversed/pending-reversal lines exist */}
            <SummaryRefundRow
              paymentLines={group.aggregatedPaymentLines ?? []}
              reference={group.subtotal ?? group.balanceDue}
            />
          </SimpleTable.Tbody>
        </SimpleTable.Table>
      </div>
    </DetailSection>
  );
};

export const SummaryRow = ({
  label,
  amount,
  isStrong,
  isError,
}: {
  label: ReactNode;
  amount: UiMoney | null;
  isStrong?: boolean;
  isError?: boolean;
}) => (
  <SimpleTable.Tr
    className={clsx(tableStyles.tableRow, isError && styles.refundRow)}
  >
    <SimpleTable.Td className={tableStyles.tableCell}>
      {isStrong ? <strong>{label}</strong> : label}
    </SimpleTable.Td>
    <SimpleTable.Td
      className={clsx(tableStyles.tableCell, tableStyles.alignRight)}
    >
      {isStrong ? <strong>{formatMoney(amount)}</strong> : formatMoney(amount)}
    </SimpleTable.Td>
  </SimpleTable.Tr>
);

/** Memoized refund row rendered in the SummarySection table. */
const SummaryRefundRow = memo(
  ({
    paymentLines,
    reference,
  }: {
    paymentLines: MarketplaceSaleLineItem[];
    reference: UiMoney | null | undefined;
  }) => {
    const refundedLines = paymentLines.filter(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    );
    if (refundedLines.length === 0) return null;
    const refundTotal = refundedLines.reduce(
      (sum, l) => sum + (l.amt?.amt ?? 0),
      0
    );
    const refundDate = refundedLines[0]?.date;
    const refundAmount: UiMoney | null = reference
      ? { ...reference, amt: -refundTotal, disp: null }
      : null;
    return (
      <SummaryRow
        label={
          <span>
            <Content id={ContentId.Refund} />
            {refundDate && (
              <span style={{ marginLeft: 8, fontWeight: 400 }}>
                {formatDate(refundDate)}
              </span>
            )}
          </span>
        }
        amount={refundAmount}
        isError
      />
    );
  }
);
SummaryRefundRow.displayName = 'SummaryRefundRow';

// ────────────────────────────────────────────────────────────────────────────
// TICKETS — flattened ticket rows (one per member sale) rendered with the shared
// TanStack `Table` (src/tables/Table) like the rest of the app, via a static
// ColumnDef array (no pagination/virtualization — the full member list is shown).
// The Event column is two-line: resolved event name (from a batched getCatalogSlim
// fetch for the rows' event ids, matched on viagogoVirtualId) + event date, since
// this standalone modal isn't guaranteed to have a populated CatalogDataContext.
// Cancelled rows are dimmed and badged.
// The Purchase column links to the ticket row's purchase order; the Sale column
// deep-links into the member sale's SaleDetails modal.
// Columns: Event | Section | Row | Seat | Qty | Cost($) | Proceed($) | Purchase | Sale
// ────────────────────────────────────────────────────────────────────────────

/** Cancelled ticket rows are dimmed (still shown; excluded from totals by backend). */
const dimClass = (row: SalesGroupTicketRow) =>
  row.isCancelled ? styles.cancelledRow : undefined;

export const TicketsSection = ({ rows }: { rows: SalesGroupTicketRow[] }) => {
  const { setModal } = useContext(ModalContext);

  // Member sales carry only a viagogoVirtualId, and this standalone modal is NOT
  // guaranteed to render inside a populated CatalogDataContext, so its
  // eventsTransformed can't be relied on to resolve event name/date. Instead
  // fetch the catalog directly for the group's ticket-row event ids (batched
  // getCatalogSlim, gated on the same Feature.SalesGroupView as this modal, and
  // deduped by React Query with the group-invoice fetch that uses the same ids).
  const eventIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows.map((r) => r.viagogoVirtualId).filter((id): id is string => !!id)
        )
      ),
    [rows]
  );
  const { data: catalog } = useGetGroupInvoiceCatalog(eventIds);

  const resolveCatalogEvent = useCallback(
    (viagogoVirtualId: string | null) => {
      if (!viagogoVirtualId) {
        return undefined;
      }
      return catalog?.events.find((e) => e.virtId === viagogoVirtualId);
    },
    [catalog]
  );

  const columns = useMemo<ColumnDef<SalesGroupTicketRow>[]>(
    () => [
      {
        id: 'event',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.Event} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => {
          const catalogEvent = resolveCatalogEvent(row.viagogoVirtualId);
          const eventStart = catalogEvent?.dates?.start;
          return (
            <TableCellDiv align="left" className={dimClass(row)}>
              <span className={styles.eventCellName}>
                {catalogEvent?.name ?? EMPTY}
              </span>
              {eventStart && (
                <span className={styles.eventCellSub}>
                  {formatDate(eventStart)}
                </span>
              )}
              {row.isCancelled && (
                <span className={styles.cancelledBadge}>
                  <Content id={ContentId.Cancelled} />
                </span>
              )}
            </TableCellDiv>
          );
        },
      },
      {
        id: 'section',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.Section} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="left" className={dimClass(row)}>
            {row.section ?? EMPTY}
          </TableCellDiv>
        ),
      },
      {
        id: 'row',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.Row} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="left" className={dimClass(row)}>
            {row.row ?? EMPTY}
          </TableCellDiv>
        ),
      },
      {
        id: 'seat',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.Seat} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="left" className={dimClass(row)}>
            {row.seats ?? EMPTY}
          </TableCellDiv>
        ),
      },
      {
        id: 'qty',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.QuantityAbbreviated} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="left" className={dimClass(row)}>
            {row.qty}
          </TableCellDiv>
        ),
      },
      {
        id: 'cost',
        header: () => (
          <TableHeaderCellDiv align="right">
            <Content id={ContentId.Cost} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="right" className={dimClass(row)}>
            {formatMoney(row.cost)}
          </TableCellDiv>
        ),
      },
      {
        id: 'proceeds',
        header: () => (
          <TableHeaderCellDiv align="right">
            <Content id={ContentId.Proceeds} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="right" className={dimClass(row)}>
            {formatMoney(row.proceed)}
          </TableCellDiv>
        ),
      },
      {
        id: 'purchase',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.Purchase} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="left" className={dimClass(row)}>
            {row.poId != null ? (
              <LinkAndCopy
                relativeUrl={getPurchaseOrderRelativeUrl(row.poId)}
                linkChildren={row.poId}
                valueToCopy={String(row.poId)}
              />
            ) : (
              EMPTY
            )}
          </TableCellDiv>
        ),
      },
      {
        id: 'sale',
        header: () => (
          <TableHeaderCellDiv align="left">
            <Content id={ContentId.Sale} />
          </TableHeaderCellDiv>
        ),
        cell: ({ row: { original: row } }) => (
          <TableCellDiv align="left" className={dimClass(row)}>
            <PosLink
              variant="brand"
              to={getSaleDetailsDeepLinkUrl(row.saleId).toString()}
              onClick={(e) => {
                e.preventDefault();
                setModal(
                  getSaleDetailsModalConfigWithDeepLink(row.saleId, true)
                );
              }}
            >
              {row.saleId}
            </PosLink>
          </TableCellDiv>
        ),
      },
    ],
    [resolveCatalogEvent, setModal]
  );

  return (
    <DetailSection name={<Content id={ContentId.Tickets} />}>
      {rows.length === 0 ? (
        <EmptySectionContent.Root>
          <Content id={ContentId.SalesGroupNoMemberSales} />
        </EmptySectionContent.Root>
      ) : (
        <Table
          options={{
            columns,
            data: rows,
            getRowId: (row) => String(row.saleId),
          }}
          roundBorders
          usePaginationFooter={false}
          disableFooter
        />
      )}
    </DetailSection>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// EARNINGS — itemized rows straight from the backend DTO (nothing recomputed in
// JS). Aggregate anchors (Subtotal, Total Proceed, Tickets Cost, P&L) bracket
// the individual charge/credit earning lines, which REPLACE the old aggregate
// Charges/Credits rows (so charges/credits are never double-counted). Rows:
// Subtotal, Total Proceed, [each earning line], Tickets Cost, P&L (green/red).
// ────────────────────────────────────────────────────────────────────────────

export const EarningsSection = ({ group }: { group: SalesGroupDetails }) => {
  const earningLines = group.earningLines ?? [];

  // Tax is already listed among the earning lines (costType Taxes) below, but the
  // backend pandL (Subtotal − TotalCost + Charges − Credits) excludes it, so fold
  // it into the displayed P&L as a positive amount so the column reconciles.
  const taxTotal = getGroupTaxAmount(group);
  const pandLWithTax: UiMoney | null = group.pandL
    ? { ...group.pandL, amt: (group.pandL.amt ?? 0) + taxTotal, disp: null }
    : group.pandL;

  return (
    <DetailSection name={<Content id={ContentId.Earnings} />}>
      <div className={tableStyles.tableWrapper}>
        <SimpleTable.Table className={tableStyles.table}>
          <SimpleTable.Thead showBottomBorder={false}>
            <SimpleTable.Tr className={tableStyles.tableRow}>
              <SimpleTable.Th className={tableStyles.tableHeader}>
                <Content id={ContentId.Overview} />
              </SimpleTable.Th>
              <SimpleTable.Th
                className={clsx(
                  tableStyles.tableHeader,
                  tableStyles.alignRight
                )}
              >
                <Content id={ContentId.Amount} />
              </SimpleTable.Th>
            </SimpleTable.Tr>
          </SimpleTable.Thead>
          <SimpleTable.Tbody>
            <EarningsRow
              label={<Content id={ContentId.Proceeds} />}
              amount={group.totalProceeds}
            />
            {earningLines.map((line, index) => (
              <EarningsRow
                key={`${line.costType}-${line.description ?? ''}-${index}`}
                label={
                  line.description ?? (
                    <Content id={SALE_COST_TYPE_TO_CONTENT_ID[line.costType]} />
                  )
                }
                amount={line.amount}
              />
            ))}
            <EarningsRow
              label={<Content id={ContentId.TicketsCost} />}
              amount={group.totalCost}
              mutedAmount
            />
            <EarningsRow
              label={<Content id={ContentId.PnL} />}
              amount={pandLWithTax}
              highlight={
                pandLWithTax
                  ? (pandLWithTax.amt ?? 0) >= 0
                    ? 'positive'
                    : 'negative'
                  : undefined
              }
            />
          </SimpleTable.Tbody>
        </SimpleTable.Table>
      </div>
    </DetailSection>
  );
};

export const EarningsRow = ({
  label,
  amount,
  highlight,
  mutedAmount,
}: {
  label: ReactNode;
  amount: UiMoney | null;
  highlight?: 'positive' | 'negative';
  mutedAmount?: boolean;
}) => (
  <SimpleTable.Tr
    className={clsx(
      tableStyles.tableRow,
      highlight === 'positive' && styles.pnlRowPositive,
      highlight === 'negative' && styles.pnlRowNegative
    )}
  >
    <SimpleTable.Td className={tableStyles.tableCell}>{label}</SimpleTable.Td>
    <SimpleTable.Td
      className={clsx(
        tableStyles.tableCell,
        tableStyles.alignRight,
        mutedAmount && styles.mutedAmount
      )}
    >
      {formatMoney(amount)}
    </SimpleTable.Td>
  </SimpleTable.Tr>
);

// ────────────────────────────────────────────────────────────────────────────
// PAYMENT — Payment Terms, aggregated payment lines, total received, and a
// Partially Paid badge. When the group has an outstanding balance and the user
// can update sales, a group-level "Add Payment" action (POS-8632) opens the
// GroupMarketplacePaymentDialog, which pays the group balance down reusing the
// single-sale "Pay Now" payment mechanics (Adyen + external, full + partial).
// ────────────────────────────────────────────────────────────────────────────

export const PaymentSection = ({ group }: { group: SalesGroupDetails }) => {
  const { setModal, currentModalConfig } = useContext(ModalContext);
  const salesGroupConfig = currentModalConfig as ModalConfig | null;

  const paymentLines = group.aggregatedPaymentLines ?? [];
  const totalReceived = group.totalPaymentReceived;

  // POS-8632: PAYMENT shows money actually RECEIVED, not the owed breakdown. A
  // received line is identified purely by state === Paid — matching the backend,
  // which sums totalPaymentReceived from Paid lines only (SalesGroupControllerService).
  // Type (e.g. a Proceeds line) does NOT distinguish owed from received: the same
  // $257 owed Proceeds line is Unpaid/Queued while the $64.25 received line is Paid.
  // Show Paid lines (money received), Reversed lines (completed refunds), and
  // PendingReversal lines (refund in progress) so the seller can see the full
  // payment + refund history, not just what's currently held.
  const refundedTotal = paymentLines
    .filter(
      (line) =>
        line.state === MarketplacePaymentState.Reversed ||
        line.state === MarketplacePaymentState.PendingReversal
    )
    .reduce((sum, line) => sum + Math.abs(line.amt?.amt ?? 0), 0);

  // POS-8953: Show per-transaction payment rows regardless of current state.
  // After a full refund all lines are Reversed, but we still display them so
  // ops can see the original payment transactions. The RefundPaymentRow below
  // separately renders the aggregated refund total, giving a complete picture:
  //   payment table = "what was paid"   (positive amounts)
  //   refund row    = "what was refunded" (negative total)
  // Exclude credit-refund split lines (created by partial refunds) — they are
  // refund artifacts, not original payment transactions, and would double-count
  // with the RefundPaymentRow summary.
  const receivedPaymentLines = paymentLines.filter(
    (line) =>
      (line.state === MarketplacePaymentState.Paid ||
        line.state === MarketplacePaymentState.Reversed ||
        line.state === MarketplacePaymentState.PendingReversal) &&
      !line.extLineId?.startsWith('credit-refund-')
  );

  // POS-8632: balanceDue is only populated by the backend once a payment has been
  // recorded, so unpaid groups have balanceDue == null. Prefer the backend value
  // when present; otherwise fall back to subtotal + earnings - paymentReceived (folds
  // charges/credits, matching the backend). Both null => 0 => button correctly hidden.
  const outstandingAmt = getOutstandingAmt(group);

  const paymentStatus = getGroupPaymentStatus(group);

  // POS-8726: check if any lines have been reversed or are pending reversal (refunded)
  // Used to conditionally render the refund summary row below.
  const hasReversedLines = paymentLines.some(
    (line) =>
      line.state === MarketplacePaymentState.Reversed ||
      line.state === MarketplacePaymentState.PendingReversal
  );

  // Representative member sale: first non-cancelled member, else the first member
  // (mirror BuyerSection). Drives both the permission gate and the saleId-keyed
  // ApplyExternalPayment endpoint used by the group dialog.
  const representativeSale =
    group.sales?.find((s) => s.cancellationReason == null) ?? group.sales?.[0];

  // Gate on the first member sale (representative). useUserCanUpdateSale must be
  // called unconditionally, so pass sales[0] (possibly undefined) directly.
  const canAddPayment = useUserCanUpdateSale(group.sales?.[0], false);

  // POS-8987: a card charge can be captured but not yet settled (async Adyen auth). The backend
  // reports its amount in unsettledPaymentAmount before it reduces the settled balance, so:
  //  - HIDE Add Payment once outstanding is covered by amount, settled or not (nothing left to pay);
  //  - DISABLE it while a charge is still settling but doesn't fully cover the balance, so the seller
  //    can't submit a second payment on top of one that's already in flight.
  const unsettledPaymentAmount = group.unsettledPaymentAmount ?? 0;
  const effectiveOutstanding = outstandingAmt - unsettledPaymentAmount;

  const showAddPayment =
    effectiveOutstanding > 0 && canAddPayment && representativeSale != null;
  const addPaymentDisabled = unsettledPaymentAmount > 0;

  const openAddPayment = () => {
    if (representativeSale == null) {
      return;
    }
    setModal({
      children: (
        <GroupMarketplacePaymentDialog
          salesGroupId={group.salesGroupId}
          remainingAmount={outstandingAmt}
          currencyCode={group.currencyCode ?? 'USD'}
          buyerEmail={group.buyer?.email ?? null}
          cancelTo={salesGroupConfig}
        />
      ),
    });
  };

  return (
    <DetailSection
      name={
        <Stack direction="column" gap="m">
          <Content id={ContentId.Payment} />
          {paymentStatus ? (
            paymentStatus === MarketplacePaymentState.Reversed ? (
              <PosPill variant={PosPillVariant.Warning}>
                <Content id={ContentId.Refunded} />
              </PosPill>
            ) : (
              <MarketplacePaymentStatePill state={paymentStatus} />
            )
          ) : (
            EMPTY
          )}
        </Stack>
      }
    >
      {receivedPaymentLines.length === 0 && refundedTotal === 0 ? (
        <EmptySectionContent.Root>
          <Content id={ContentId.SalesGroupNoPayments} />
        </EmptySectionContent.Root>
      ) : receivedPaymentLines.length > 0 ? (
        <div className={tableStyles.tableWrapper}>
          <SimpleTable.Table className={tableStyles.table}>
            <SimpleTable.Thead showBottomBorder={false}>
              <SimpleTable.Tr className={tableStyles.tableRow}>
                <SimpleTable.Th className={tableStyles.tableHeader}>
                  <Content id={ContentId.Reference} />
                </SimpleTable.Th>
                <SimpleTable.Th className={tableStyles.tableHeader}>
                  <Content id={ContentId.Date} />
                </SimpleTable.Th>
                <SimpleTable.Th className={tableStyles.tableHeader}>
                  <Content id={ContentId.PaymentMethod} />
                </SimpleTable.Th>
                <SimpleTable.Th
                  className={clsx(
                    tableStyles.tableHeader,
                    tableStyles.alignRight
                  )}
                >
                  <Content id={ContentId.Amount} />
                </SimpleTable.Th>
              </SimpleTable.Tr>
            </SimpleTable.Thead>
            <SimpleTable.Tbody>
              {receivedPaymentLines.map((line, index) => (
                <PaymentLineRow
                  key={`${line.posPmtId}-${line.type}-${index}`}
                  line={line}
                />
              ))}
            </SimpleTable.Tbody>
          </SimpleTable.Table>
        </div>
      ) : null}
      <SectionContent numOfColumns={1}>
        <Stack justifyContent="spaceBetween" alignItems="center">
          <DetailGroup>
            <Detail
              label={<Content id={ContentId.Payment} />}
              detail={formatMoney(totalReceived)}
              isStrong
            />
          </DetailGroup>
          <Stack alignItems="center" gap="s">
            {/* POS-8726: Refund summary row — shows when any reversed/pending-reversal lines exist */}
            {hasReversedLines && (
              <RefundPaymentRow
                paymentLines={paymentLines}
                reference={group.subtotal}
              />
            )}
            {showAddPayment && (
              <OkButton
                textContentId={ContentId.AddPayment}
                variant="outlineBrand"
                size="md"
                onClick={openAddPayment}
                disabled={addPaymentDisabled}
              />
            )}
          </Stack>
        </Stack>
      </SectionContent>
    </DetailSection>
  );
};

/** Memoized refund summary row rendered below the payment total in PaymentSection. */
const RefundPaymentRow = memo(
  ({
    paymentLines,
    reference,
  }: {
    paymentLines: MarketplaceSaleLineItem[];
    reference: UiMoney | null | undefined;
  }) => {
    const reversedLines = paymentLines.filter(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    );
    if (reversedLines.length === 0) return null;
    const refundTotal = reversedLines.reduce(
      (sum, l) => sum + (l.amt?.amt ?? 0),
      0
    );
    const refundDate = reversedLines[0]?.date;
    // Refund to credit always credits the buyer's account balance.
    const refundPmtMethodCid = ContentId.PaidViaCustomerCredit;
    const refundAmount: UiMoney | null = reference
      ? { ...reference, amt: -refundTotal, disp: null }
      : null;
    return (
      <div className={tableStyles.tableWrapper}>
        <SimpleTable.Table className={tableStyles.table}>
          <SimpleTable.Tbody>
            <SimpleTable.Tr
              className={clsx(tableStyles.tableRow, styles.refundRow)}
            >
              <SimpleTable.Td className={tableStyles.tableCell}>
                <strong>
                  <Content id={ContentId.Refund} />
                </strong>
              </SimpleTable.Td>
              <SimpleTable.Td className={tableStyles.tableCell}>
                {EMPTY}
              </SimpleTable.Td>
              <SimpleTable.Td className={tableStyles.tableCell}>
                {formatDate(refundDate)}
              </SimpleTable.Td>
              <SimpleTable.Td className={tableStyles.tableCell}>
                <Content id={refundPmtMethodCid} />
              </SimpleTable.Td>
              <SimpleTable.Td
                className={clsx(tableStyles.tableCell, tableStyles.alignRight)}
              >
                <strong>{formatMoney(refundAmount)}</strong>
              </SimpleTable.Td>
            </SimpleTable.Tr>
          </SimpleTable.Tbody>
        </SimpleTable.Table>
      </div>
    );
  }
);
RefundPaymentRow.displayName = 'RefundPaymentRow';

export const PaymentLineRow = ({ line }: { line: MarketplaceSaleLineItem }) => {
  const reference = line.extPmtId ?? line.pmtRefId ?? EMPTY;

  return (
    <SimpleTable.Tr className={tableStyles.tableRow}>
      <SimpleTable.Td className={tableStyles.tableCell}>
        {line.posPmtId ? (
          <PosLink
            variant="brand"
            to={getMarketplacePaymentRelativeUrl(line.posPmtId)}
          >
            {reference}
          </PosLink>
        ) : (
          <>{reference}</>
        )}
      </SimpleTable.Td>
      <SimpleTable.Td className={tableStyles.tableCell}>
        {formatDate(line.date)}
      </SimpleTable.Td>
      <SimpleTable.Td className={tableStyles.tableCell}>
        {line.extPmtMethodType != null ? (
          line.extCustomPmtTypeName &&
          line.extPmtMethodType === ExternalPaymentMethodType.Other ? (
            <>{line.extCustomPmtTypeName}</>
          ) : (
            <Content
              id={
                MARKETPLACE_EXTERNAL_PAYMENT_TYPE_TO_CID[
                  line.extPmtMethodType as ExternalPaymentMethodType
                ]
              }
            />
          )
        ) : (
          <Content id={ContentId.CreditCard} />
        )}
      </SimpleTable.Td>
      <SimpleTable.Td
        className={clsx(tableStyles.tableCell, tableStyles.alignRight)}
      >
        {formatMoney(line.amt)}
      </SimpleTable.Td>
    </SimpleTable.Tr>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// BUYER DELIVERY — group-level buyer contact (name / email / phone), sourced by
// the backend from a member sale's transfer destination. Shown ONCE for the
// group (mirrors SaleDetailsV2 DeliverySection's "Your Buyer" block, using the
// same responsive SectionContentGridFormat). Omitted when the group has no buyer
// (e.g. non-retail groups).
//
// Ticket Type / Status / In-Hand from the Figma "Buyer Delivery" ARE now shown
// alongside the buyer contact. SalesGroupDetails exposes no group-level delivery
// attributes — these live per-member on Sale — so we source them from a
// representative member sale: the first non-cancelled member (falling back to
// the first member when every member is cancelled). Each row is rendered only
// when its underlying value is present, so partial data degrades gracefully.
// Per-sale delivery detail remains reachable by drilling into a member sale via
// SaleDetailsV2.
// ────────────────────────────────────────────────────────────────────────────

export const BuyerSection = ({ group }: { group: SalesGroupDetails }) => {
  const buyer = group.buyer;
  if (!buyer) {
    return null;
  }

  // Representative member sale: first non-cancelled member, else the first member.
  const representativeSale =
    group.sales?.find((s) => s.cancellationReason == null) ?? group.sales?.[0];

  return (
    <DetailSection name={<Content id={ContentId.BuyerDelivery} />}>
      <SectionContent numOfColumns={4}>
        <DetailGroup>
          <Detail
            label={<Content id={ContentId.YourBuyer} />}
            detail={
              <div>
                <span className={styles.buyerContactLine}>{buyer.name}</span>
                <span className={styles.buyerContactLine}>{buyer.email}</span>
                <span className={styles.buyerContactLine}>
                  {buyer.phoneNumber}
                </span>
              </div>
            }
          />
        </DetailGroup>
        {representativeSale?.inHandAt && (
          <DetailGroup>
            <Detail
              label={<Content id={ContentId.InHand} />}
              detail={formatDate(representativeSale.inHandAt)}
            />
          </DetailGroup>
        )}
      </SectionContent>
    </DetailSection>
  );
};
